import { useState, useCallback } from 'react';
import { Text, Button, Badge, Tab, TabList, Menu, MenuTrigger, MenuPopover, MenuList, MenuItem } from '@fluentui/react-components';
import { DocumentRegular, ArrowLeftRegular, ArrowDownloadRegular, CheckmarkCircleRegular, EditRegular, EyeRegular, ArrowSyncRegular } from '@fluentui/react-icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { MethodologyDocument } from '../services/document-service';
import { updateDocumentStatus } from '../services/document-service';
import { apiUrl } from '../utils/api';
import { useApiToken } from '../hooks/useApiToken';

const STATUS_COLOURS: Record<string, 'warning' | 'success' | 'brand'> = {
  draft: 'warning',
  review: 'brand',
  final: 'success',
};

const STATUS_ICONS: Record<string, typeof EditRegular> = {
  draft: EditRegular,
  review: EyeRegular,
  final: CheckmarkCircleRegular,
};

const NEXT_STATUS: Record<string, string> = {
  draft: 'review',
  review: 'final',
};

interface DocumentViewerPageProps {
  document: MethodologyDocument;
  onBack: () => void;
  onDownloadPdf?: () => void;
  onStatusChange?: (docId: string, newStatus: string) => void;
}

export function DocumentViewerPage({ document, onBack, onDownloadPdf, onStatusChange }: DocumentViewerPageProps) {
  const getToken = useApiToken();
  const [activeTab, setActiveTab] = useState('rendered');
  const [currentStatus, setCurrentStatus] = useState(document.status);
  const [updating, setUpdating] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [completeness, setCompleteness] = useState<number | null>(null);
  const [content, setContent] = useState(document.markdownContent);

  const handleRegenerate = useCallback(async () => {
    setRegenerating(true);
    try {
      const token = await getToken();
      const res = await fetch(apiUrl(`/api/documents/${document.id}/regenerate?customerId=${encodeURIComponent(document.customerId)}`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Regenerate failed: ${res.status}`);
      const result = await res.json();
      setCompleteness(result.completeness);
      // Reload the document content
      const docRes = await fetch(apiUrl(`/api/documents/${document.id}?customerId=${encodeURIComponent(document.customerId)}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (docRes.ok) {
        const updatedDoc = await docRes.json();
        setContent(updatedDoc.markdownContent);
      }
    } catch (err) {
      console.error('Regenerate failed:', err);
    } finally {
      setRegenerating(false);
    }
  }, [document.id, document.customerId, getToken]);

  const handleStatusChange = async (newStatus: string, force = false) => {
    setUpdating(true);
    try {
      const token = await getToken();
      await updateDocumentStatus(document.id!, document.customerId, newStatus, token, force);
      setCurrentStatus(newStatus);
      onStatusChange?.(document.id!, newStatus);
    } catch (err) {
      console.error('Status update failed:', err);
    } finally {
      setUpdating(false);
    }
  };

  const nextStatus = NEXT_STATUS[currentStatus];
  const StatusIcon = STATUS_ICONS[currentStatus] ?? EditRegular;

  return (
    <div className="page-root page-root--scrollable document-viewer-page">
      <div className="page-title-bar">
        <Button appearance="subtle" icon={<ArrowLeftRegular />} onClick={onBack} title="Back to documents" />
        <DocumentRegular className="icon-title" />
        <Text size={500} weight="semibold">{document.title}</Text>
        <div className="title-bar-actions">
          {/* Status workflow controls */}
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button
                appearance="outline"
                size="small"
                icon={<StatusIcon />}
                disabled={updating}
              >
                <Badge size="small" color={STATUS_COLOURS[currentStatus] ?? 'warning'}>{currentStatus}</Badge>
              </Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                {nextStatus && (
                  <MenuItem icon={<CheckmarkCircleRegular />} onClick={() => handleStatusChange(nextStatus)}>
                    Promote to {nextStatus}
                  </MenuItem>
                )}
                {currentStatus === 'final' && (
                  <MenuItem icon={<EditRegular />} onClick={() => handleStatusChange('draft', true)}>
                    Revert to draft
                  </MenuItem>
                )}
                {currentStatus === 'review' && (
                  <MenuItem icon={<EditRegular />} onClick={() => handleStatusChange('draft', true)}>
                    Back to draft
                  </MenuItem>
                )}
              </MenuList>
            </MenuPopover>
          </Menu>

          <Button
            appearance="subtle"
            icon={<ArrowSyncRegular />}
            onClick={handleRegenerate}
            disabled={regenerating}
            size="small"
            title="Regenerate from registers"
          >
            {regenerating ? 'Regenerating...' : 'Regenerate'}
          </Button>
          {completeness !== null && (
            <Badge size="small" color={completeness >= 0.8 ? 'success' : completeness >= 0.5 ? 'warning' : 'danger'}>
              {Math.round(completeness * 100)}% complete
            </Badge>
          )}
          <Badge size="small" appearance="outline">{document.cafPhase}</Badge>
          <Badge size="small" appearance="outline">v{document.version}</Badge>
          {onDownloadPdf && (
            <Button appearance="subtle" icon={<ArrowDownloadRegular />} onClick={onDownloadPdf} title="Download PDF" />
          )}
        </div>
      </div>

      <div className="viewer-tabs">
        <TabList selectedValue={activeTab} onTabSelect={(_e, d) => setActiveTab(d.value as string)}>
          <Tab value="rendered">Rendered</Tab>
          <Tab value="markdown">Markdown</Tab>
        </TabList>
      </div>

      <div className="viewer-content">
        {activeTab === 'rendered' && (
          <div className="markdown-body">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                img: ({ ...props }) => (
                  <img
                    {...props}
                    style={{ maxWidth: '360px', height: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '8px', background: 'white', display: 'block', margin: '1em 0' }}
                  />
                ),
              }}
            >
              {content}
            </ReactMarkdown>
          </div>
        )}
        {activeTab === 'markdown' && (
          <pre className="markdown-source">{document.markdownContent}</pre>
        )}
      </div>
    </div>
  );
}
